import React, { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, FileTextIcon } from 'lucide-react';
import ActionMenu, { ActionMenuItem } from './ActionMenu';
import EntityStatusBadge from './EntityStatusBadge';

type ClientStatus = 'Active' | 'Lead' | 'Past';

export interface MetadataItem {
  icon: ReactNode;
  text: string;
}

export interface PrimaryAction {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  loading?: boolean;
  variant?: 'primary' | 'success' | 'danger';
}

export interface SecondaryAction {
  icon: ReactNode;
  onClick: () => void;
  ariaLabel: string;
  variant?: 'default' | 'primary';
}

interface ActionCardProps {
  icon: ReactNode;
  iconBgColor: string;
  iconColor: string;
  clientName?: string; // Optional for single-client contexts
  clientStatus?: ClientStatus; // Optional for single-client contexts
  clientId?: string; // Optional client ID for navigation
  title: string | ReactNode;
  metadata: MetadataItem[];
  primaryAction?: PrimaryAction;
  secondaryActions?: SecondaryAction[];
  menuItems?: ActionMenuItem[];
  statusBadge?: ReactNode;
  cardState?: 'default' | 'completed' | 'cancelled';
  className?: string;
  showClientInfo?: boolean; // Control whether to show client name/status
  actionsLayout?: 'bottom' | 'inline'; // Position of action buttons
  // Plan linking (for reminders)
  planId?: string | null;
  planTitle?: string | null;
  onPlanClick?: (planId: string) => void;
}

export default function ActionCard({
  icon,
  iconBgColor,
  iconColor,
  clientName,
  clientStatus,
  clientId,
  title,
  metadata,
  primaryAction,
  secondaryActions = [],
  menuItems = [],
  statusBadge,
  cardState = 'default',
  className = '',
  showClientInfo = true,
  actionsLayout = 'bottom',
  planId,
  planTitle,
  onPlanClick,
}: ActionCardProps) {
  const navigate = useNavigate();

  const borderColorClass =
    cardState === 'completed'
      ? 'border-gray-300'
      : cardState === 'cancelled'
      ? 'border-red-200'
      : 'border-gray-100';

  const handleClientNameClick = (e: React.MouseEvent) => {
    if (clientId) {
      e.stopPropagation();
      navigate(`/client/${clientId}`);
    }
  };

  const handlePlanClick = (e: React.MouseEvent) => {
    if (planId && onPlanClick) {
      e.stopPropagation();
      onPlanClick(planId);
    }
  };

  const primaryVariantClass =
    primaryAction?.variant === 'danger'
      ? 'bg-red-600 hover:bg-red-700'
      : primaryAction?.variant === 'success'
      ? 'bg-[#5B8E87] hover:bg-[#4a726c]'
      : 'bg-[#5B8E87] hover:bg-[#4a726c]';

  // Render action buttons (used inline or at bottom)
  const renderActions = (inlineMode = false) => {
    if (!primaryAction && secondaryActions.length === 0) return null;

    return (
      <div className={`flex items-center gap-2 ${inlineMode ? '' : 'flex-col sm:flex-row mt-3'}`}>
        {/* Primary Action */}
        {primaryAction && (
          <button
            onClick={primaryAction.onClick}
            disabled={primaryAction.loading}
            aria-label={primaryAction.label}
            className={`${
              inlineMode
                ? 'w-8 h-8 sm:w-auto sm:h-auto sm:py-1.5 sm:px-3 text-sm'
                : 'w-10 h-10 sm:w-auto sm:h-auto sm:py-2 sm:px-4'
            } ${primaryVariantClass} text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center sm:gap-2`}
          >
            {primaryAction.loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              primaryAction.icon
            )}
            <span className="hidden sm:inline">{primaryAction.label}</span>
          </button>
        )}

        {/* Secondary Actions */}
        {secondaryActions.map((action, index) => (
          <button
            key={index}
            onClick={action.onClick}
            aria-label={action.ariaLabel}
            className={`flex-shrink-0 ${
              inlineMode ? 'w-8 h-8' : 'w-10 h-10'
            } flex items-center justify-center rounded-lg transition-colors ${
              action.variant === 'primary'
                ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {action.icon}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div
      className={`bg-white rounded-2xl shadow-sm p-4 border ${borderColorClass} hover:shadow-md transition-shadow ${className}`}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${iconBgColor}`}
        >
          <div className={iconColor}>{icon}</div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Client Name + Status + Actions/Menu */}
          {showClientInfo && clientName && (
            <>
              {/* Mobile: Client name + Menu only on first row */}
              <div className="flex sm:hidden items-center gap-2 mb-1">
                {clientId ? (
                  <h3
                    className="font-semibold text-[#212121] truncate flex-1 cursor-pointer hover:text-[#5B8E87] transition-colors"
                    onClick={handleClientNameClick}
                  >
                    {clientName}
                  </h3>
                ) : (
                  <h3 className="font-semibold text-[#212121] truncate flex-1">{clientName}</h3>
                )}
                {menuItems.length > 0 && <ActionMenu items={menuItems} />}
              </div>
              {/* Mobile: Badges + Actions on second row */}
              {actionsLayout === 'inline' && (
                <div className="flex sm:hidden items-center gap-2 mb-2">
                  {clientStatus && <EntityStatusBadge type="client" status={clientStatus} />}
                  {statusBadge}
                  <div className="ml-auto flex items-center gap-2">
                    {renderActions(true)}
                  </div>
                </div>
              )}
              {/* Desktop: Everything on one row */}
              <div className="hidden sm:flex items-center gap-2 mb-1">
                {clientId ? (
                  <h3
                    className="font-semibold text-[#212121] truncate cursor-pointer hover:text-[#5B8E87] transition-colors"
                    onClick={handleClientNameClick}
                  >
                    {clientName}
                  </h3>
                ) : (
                  <h3 className="font-semibold text-[#212121] truncate">{clientName}</h3>
                )}
                {clientStatus && <EntityStatusBadge type="client" status={clientStatus} />}
                {statusBadge}
                <div className="ml-auto flex-shrink-0 flex items-center gap-2">
                  {actionsLayout === 'inline' && renderActions(true)}
                  {menuItems.length > 0 && <ActionMenu items={menuItems} />}
                </div>
              </div>
            </>
          )}

          {/* Title/Message - with actions inline or menu only */}
          <div className="flex items-start gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {typeof title === 'string' ? <div className="text-sm text-gray-600">{title}</div> : title}
                {planId && planTitle && onPlanClick && (
                  <button
                    onClick={handlePlanClick}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#5B8E87] bg-opacity-10 rounded-full hover:bg-opacity-20 transition-colors"
                  >
                    <FileTextIcon className="w-3 h-3 text-[#5B8E87]" />
                    <span className="text-xs text-[#5B8E87] font-medium">
                      {planTitle}
                    </span>
                  </button>
                )}
              </div>
            </div>
            {!showClientInfo && (
              <div className="flex items-center gap-2 flex-shrink-0">
                {statusBadge}
                {actionsLayout === 'inline' && renderActions(true)}
                {menuItems.length > 0 && <ActionMenu items={menuItems} />}
              </div>
            )}
          </div>

          {/* Metadata */}
          {metadata.length > 0 && (
            <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
              {metadata.map((item, index) => (
                <div key={index} className="flex items-center gap-1">
                  {item.icon}
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Actions at Bottom (only when actionsLayout is 'bottom') */}
      {actionsLayout === 'bottom' && renderActions(false)}
    </div>
  );
}
